/* eslint-disable max-lines */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import { format } from 'date-fns';
import { map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveFulfillingPharmacies } from 'store/selectors/facilitySelectors';
import useRoles from 'common/hooks/useRoles';
import colors from 'common/constants/colors';
import OFFAGE_REASON_OPTIONS from 'common/constants/offageReasons';
import TALLY_REASON_OPTIONS from 'common/constants/tallyReasons';
import { listReceivingsByPage } from 'store/thunks/stockManagementThunk';
import {
  createInventoryReceivingLineSplitsApi,
  inventoryLotsByLotIdApi,
  listInventoryReceivingLineSplitsApi,
  updateInventoryReceivingLineSplitQuantityApi,
  verifyInventoryReceivingLineSplitApi,
} from 'api/inventory';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import {
  formatPreviewQty,
  formatUnverifiedQty,
  getLotEntriesForNdc,
  getTallyState,
  getUnverifiedAtPharmacy,
  isTallyComplete,
} from '../utils/stockLineValidation';

const PRIMARY = '#031c57';
const PRIMARY_LIGHT = '#eef1f8';

let verifyOffageIdSeq = 0;
const newVerifyOffageEntry = () => {
  verifyOffageIdSeq += 1;
  return { id: verifyOffageIdSeq, qty: '', reasons: [] };
};

const isOffageDirty = (entry) => String(entry.qty).trim() !== '' || entry.reasons.length > 0;

const SPLIT_STATUS_STYLES = {
  PENDING_VERIFICATION: { bgcolor: '#ed6c02', color: '#fff' },
  VERIFIED: { bgcolor: '#2e7d32', color: '#fff' },
};

const headCellSx = {
  color: '#fff',
  fontWeight: 600,
  py: 1.25,
  borderBottom: '1px solid rgba(255,255,255,0.15)',
};

const EDIT_QTY_ERROR_MESSAGES = {
  SPLIT_NOT_FOUND: 'This split could not be found. Please refresh and try again.',
  SPLIT_ALREADY_VERIFIED: 'This split has already been verified and cannot be modified.',
  INVALID_SPLIT_QUANTITY: 'Quantity must be at least 1.',
  OVER_ALLOCATION:
    'This quantity would exceed the total received for this line. Please reduce the amount.',
  FAILED_TO_UPDATE_INVENTORY_RECEIVING_LINE_SPLIT_QUANTITY:
    'An unexpected server error occurred. Please try again.',
};

const getEditQtyErrorMessage = (err) => {
  const code = err?.response?.errors?.[0]?.extensions?.code;
  return EDIT_QTY_ERROR_MESSAGES[code] ?? 'Failed to update quantity. Please try again.';
};

/**
 * POM-6173 — why a verification came back, in the operator's terms.
 *
 * The settlement refusals are the reason this exists: every one of them names a number or a date
 * that can be corrected on the spot, and all of them used to arrive as "Failed to confirm qty
 * received" — which describes the verification failing when the verification was fine, and says
 * nothing about the correction that was actually refused.
 */
const VERIFY_ERROR_MESSAGES = {
  SPLIT_NOT_FOUND: 'This distribution could not be found. Please refresh and try again.',
  SPLIT_ALREADY_VERIFIED: 'This distribution has already been verified.',
  INVALID_SPLIT_QUANTITY: 'Qty Verified cannot be negative or exceed Qty Expected.',
  INVENTORY_TALLY_BUD_REQUIRED:
    `A BUD is required on the receiving line to settle a ${SYSTEM_DEFICIT.toLowerCase()} quantity. The expiration ` +
    'date is part of the lot identity, so without it this delivery cannot be matched to the lot.',
  INVENTORY_UNVERIFIED_REASON_REQUIRED: `A reason is required to settle a ${SYSTEM_DEFICIT.toLowerCase()} quantity. Choose one and try again.`,
  INVENTORY_TALLY_LOT_MISMATCH:
    `That lot no longer carries a ${SYSTEM_DEFICIT.toLowerCase()} quantity at this facility, or does not match this ` +
    'line. Reopen the distribution to see the current lot details.',
  INVENTORY_TALLY_BUD_CONFLICT:
    "The BUD on this line does not match the existing lot's own expiration date. Correct the " +
    'receipt, or adjust the lot separately in Inventory.',
  INVENTORY_TALLY_LOT_MERGE_REQUIRED:
    'This lot exists twice for the same product and expiration date at this facility. The two ' +
    `records refer to the same physical lot and must be merged before the ${SYSTEM_DEFICIT.toLowerCase()} quantity ` +
    'can be settled.',
  INVENTORY_TALLY_EXCEEDS_BALANCE:
    'That is more than this lot can account for — a settlement cannot exceed either the ' +
    `${SYSTEM_DEFICIT.toLowerCase()} quantity or the quantity being verified.`,
  INVENTORY_UNVERIFIED_OVER_RESOLVE:
    `This lot no longer carries that much ${SYSTEM_DEFICIT.toLowerCase()} quantity — another user may have resolved ` +
    'it. Reopen the distribution to see the current amounts.',
};

const getVerifyErrorMessage = (err) => {
  const code = err?.response?.errors?.[0]?.extensions?.code;
  return VERIFY_ERROR_MESSAGES[code] ?? 'Failed to confirm qty received. Please try again.';
};

const LineSplitsDialog = ({ line, receivedAtPharmacyId, open, onClose }) => {
  const dispatch = useDispatch();
  const hasAccess = useRoles();
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // POM-5827: active-only allowed pharmacies (auth list can include inactive ones).
  const fulfillingPharmacies = useSelector(selectActiveFulfillingPharmacies);

  // Verifying a distribution is what makes the stock real at the destination, so it belongs to
  // someone who works there. selectActiveFulfillingPharmacies is already the caller's own allowed
  // facilities, which is the same list the server checks against.
  const userFacilityIds = useMemo(
    () => new Set(map(fulfillingPharmacies, 'fulfillingPharmacyId')),
    [fulfillingPharmacies]
  );
  const currentPage = useSelector(({ inventory }) => inventory.stockManagement?.currentPage ?? 1);

  const [addOpen, setAddOpen] = useState(false);
  const [newSplits, setNewSplits] = useState([{ fulfillingPharmacyId: '', quantityExpected: '' }]);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addShowError, setAddShowError] = useState(false);

  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyQty, setVerifyQty] = useState('');
  const [verifyOffageReasons, setVerifyOffageReasons] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyShowError, setVerifyShowError] = useState(false);

  // POM-6173 — every facility this lot number exists at, so the claim held at the facility being
  // verified can be read off it. The lookup is deliberately cross-facility and cross-NDC (that is
  // what catches a lot recorded against the wrong drug), so it is narrowed before it is used.
  const [lotEntries, setLotEntries] = useState([]);
  const [verifyTallyQty, setVerifyTallyQty] = useState(null);
  const [verifyTallyReason, setVerifyTallyReason] = useState('');
  const [verifyTallyNotes, setVerifyTallyNotes] = useState('');

  const [editTarget, setEditTarget] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editShowError, setEditShowError] = useState(false);

  const [howItWorksAnchor, setHowItWorksAnchor] = useState(null);

  const existingAllocated = splits.reduce(
    (sum, s) => sum + (s.quantityVerified ?? s.quantityExpected),
    0
  );
  const totalOffage = splits.reduce((sum, s) => sum + (s.offage ?? 0), 0);
  const hasQtyLimit = line?.totalQuantityReceived != null;
  const remaining = hasQtyLimit
    ? line.totalQuantityReceived - existingAllocated + totalOffage
    : null;

  let availableQtyColor = 'text.primary';
  if (!loading && remaining !== null) {
    if (remaining > 0) availableQtyColor = '#2e7d32';
    else if (remaining === 0) availableQtyColor = '#ed6c02';
    else availableQtyColor = 'error.main';
  }

  const verifyQtyFilled = String(verifyQty).trim() !== '';
  const verifyQtyInvalid = verifyShowError && (!verifyQtyFilled || Number(verifyQty) < 0);
  const verifyQtyOverExpected =
    verifyQtyFilled &&
    verifyTarget?.quantityExpected != null &&
    Number(verifyQty) > verifyTarget.quantityExpected;
  let verifyQtyHelperText = '';
  if (verifyQtyInvalid) verifyQtyHelperText = 'Must be ≥ 0';
  else if (verifyQtyOverExpected)
    verifyQtyHelperText = `Qty Verified exceeds Qty Expected (${verifyTarget?.quantityExpected}) — over-delivery`;

  /**
   * POM-6173 — the unverified balance on THIS facility's row of this lot.
   *
   * Scoped to the split's own facility, not the receiving one: a claim belongs to one lot row at
   * one site, and verifying a delivery into North Carolina is not evidence for stock in Michigan.
   * Narrowed to the line's NDC first, because the lookup answers for the lot NUMBER and the same
   * number can exist against another drug — settling that lot's claim from here would credit a
   * different product.
   *
   * A tally also needs the line's BUD (expiration_date is part of the lot's identity, so the API
   * cannot match the delivery to the lot without it). With no BUD on the line there is nothing to
   * offer, and the notice below says so rather than presenting a control that can only fail.
   */
  const verifyUnverified =
    verifyTarget && line?.lotNumber
      ? getUnverifiedAtPharmacy({
          entries: getLotEntriesForNdc({ entries: lotEntries, ndc: line?.ndc }),
          // The list query returns the facility as a nested object, not a flat id — the flat
          // column exists only on the server's own entity.
          fulfillingPharmacyId: verifyTarget.fulfillingPharmacy?.fulfillingPharmacyId,
        })
      : null;
  const canSettleUnverified = Boolean(verifyUnverified && line?.bud);
  // Capped by the quantity being VERIFIED, not the quantity expected: the settlement can only be
  // evidenced by units that actually arrived.
  const verifyTallyState = canSettleUnverified
    ? getTallyState({
        unverifiedQty: verifyUnverified.unverifiedQty,
        quantityReceived: verifyQty,
        tallyQty: verifyTallyQty,
      })
    : null;
  const verifyTallyComplete =
    !canSettleUnverified ||
    isTallyComplete({
      unverifiedQty: verifyUnverified.unverifiedQty,
      quantityReceived: verifyQty,
      tallyQty: verifyTallyQty,
      tallyReason: verifyTallyReason,
    });
  const verifyTallyReasonMissing = Boolean(
    verifyTallyState && verifyTallyState.effective > 0 && !String(verifyTallyReason).trim()
  );

  const verifyOffage =
    verifyQtyFilled && !verifyQtyOverExpected && verifyTarget?.quantityExpected != null
      ? verifyTarget.quantityExpected - Number(verifyQty)
      : 0;
  const showVerifyOffageSection = verifyOffage > 0;
  const verifyDirtyReasons = verifyOffageReasons.filter(isOffageDirty);
  const verifyReasonQtySum = verifyDirtyReasons.reduce((s, r) => s + (Number(r.qty) || 0), 0);
  const verifyReasonsBalanced = verifyOffage === 0 || verifyReasonQtySum === verifyOffage;
  const verifyOffageRequiredValid =
    verifyOffage === 0 ||
    (verifyDirtyReasons.length > 0 &&
      verifyReasonsBalanced &&
      verifyDirtyReasons.every((r) => Number(r.qty) >= 1 && r.reasons.length > 0));

  const fetchSplits = (receivingLineId) => {
    setLoading(true);
    setFetchError(null);
    listInventoryReceivingLineSplitsApi({ receivingLineId })
      .then(({ items }) => setSplits(items))
      .catch(() => setFetchError('Failed to load splits.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !line?.receivingLineId) return;
    setSplits([]);
    fetchSplits(line.receivingLineId);
  }, [open, line?.receivingLineId]);

  // POM-6173 — read once per open, not per verification: the answer is the same for every split
  // of the line, and it decides whether a settlement is offered at all. A failure leaves the
  // entries empty, which simply means no settlement is offered — verification still works, and
  // the balance can still be corrected from the Inventory dashboard.
  useEffect(() => {
    if (!open || !line?.lotNumber) {
      setLotEntries([]);
      return;
    }
    inventoryLotsByLotIdApi({ lotId: line.lotNumber })
      .then((entries) => setLotEntries(entries ?? []))
      .catch(() => setLotEntries([]));
  }, [open, line?.lotNumber]);

  const resetVerifyTally = () => {
    setVerifyTallyQty(null);
    setVerifyTallyReason('');
    setVerifyTallyNotes('');
  };

  const handleOpenVerify = (split) => {
    setVerifyTarget(split);
    setVerifyQty('');
    setVerifyOffageReasons([]);
    setVerifyShowError(false);
    resetVerifyTally();
  };

  const handleCloseVerify = () => {
    setVerifyTarget(null);
    setVerifyQty('');
    setVerifyOffageReasons([]);
    setVerifyShowError(false);
    resetVerifyTally();
  };

  const handleAddVerifyOffageReason = () =>
    setVerifyOffageReasons((prev) => [...prev, newVerifyOffageEntry()]);

  const handleRemoveVerifyOffageReason = (idx) =>
    setVerifyOffageReasons((prev) => prev.filter((_, i) => i !== idx));

  const handleVerifyOffageReasonQtyChange = (idx, value) =>
    setVerifyOffageReasons((prev) => prev.map((r, i) => (i === idx ? { ...r, qty: value } : r)));

  const handleVerifyOffageReasonTagsChange = (idx, newTags) =>
    setVerifyOffageReasons((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, reasons: newTags } : r))
    );

  const handleVerifySave = () => {
    if (String(verifyQty).trim() === '' || Number(verifyQty) < 0) {
      setVerifyShowError(true);
      return;
    }
    setVerifying(true);

    const validOffageReasons = verifyDirtyReasons
      .filter((r) => Number(r.qty) >= 1 && r.reasons.length > 0)
      .map((r) => ({ qty: Number(r.qty), reasons: r.reasons }));

    verifyInventoryReceivingLineSplitApi({
      splitId: verifyTarget.splitId,
      quantityVerified: Number(verifyQty),
      ...(validOffageReasons.length > 0 && { offageReasons: validOffageReasons }),
      ...(canSettleUnverified
        ? {
            unverifiedTally: {
              // The lot ID, not the lot number: the credit has to land on the exact row the
              // claim sits on, and the same number exists at other facilities.
              inventoryLotId: verifyUnverified.inventoryLotId,
              quantity: verifyTallyState.effective ?? 0,
              // `reason` is a GraphQL ENUM, so an empty string is a request-level validation
              // error rather than a null — it has to be omitted, not blanked. It is only ever
              // absent on a zero tally, which the API accepts without one.
              ...(String(verifyTallyReason).trim() ? { reason: verifyTallyReason.trim() } : {}),
              ...(String(verifyTallyNotes).trim() ? { notes: verifyTallyNotes.trim() } : {}),
            },
          }
        : {}),
    })
      .then((updated) => {
        setSplits((prev) => prev.map((s) => (s.splitId === updated.splitId ? updated : s)));
        dispatch(listReceivingsByPage({ page: currentPage }));
        // The claim this settled is gone, so the cached answer describing it is stale — and the
        // dialog stays open on the other splits of the same line.
        if (canSettleUnverified && (verifyTallyState.effective ?? 0) > 0 && line?.lotNumber) {
          inventoryLotsByLotIdApi({ lotId: line.lotNumber })
            .then((entries) => setLotEntries(entries ?? []))
            .catch(() => setLotEntries([]));
        }
        handleCloseVerify();
      })
      .catch((err) => enqueueSnackbar(getVerifyErrorMessage(err), { variant: 'error' }))
      .finally(() => setVerifying(false));
  };

  const handleOpenEdit = (split) => {
    setEditTarget(split);
    setEditQty(String(split.quantityExpected));
    setEditError(null);
    setEditShowError(false);
  };

  const handleCloseEdit = () => {
    setEditTarget(null);
    setEditQty('');
    setEditError(null);
    setEditShowError(false);
  };

  const handleEditSave = () => {
    if (String(editQty).trim() === '' || Number(editQty) < 1) {
      setEditShowError(true);
      return;
    }
    setEditing(true);
    setEditError(null);
    updateInventoryReceivingLineSplitQuantityApi({
      splitId: editTarget.splitId,
      quantityExpected: Number(editQty),
    })
      .then((updated) => {
        setSplits((prev) => prev.map((s) => (s.splitId === updated.splitId ? updated : s)));
        handleCloseEdit();
      })
      .catch((err) => setEditError(getEditQtyErrorMessage(err)))
      .finally(() => setEditing(false));
  };

  const editQtyInvalid = editShowError && (String(editQty).trim() === '' || Number(editQty) < 1);

  const newSplitTotal = newSplits.reduce((sum, s) => sum + (Number(s.quantityExpected) || 0), 0);
  const newSplitOverflow =
    remaining !== null &&
    newSplits.some((s) => s.quantityExpected !== '') &&
    newSplitTotal > remaining;

  const handleOpenAdd = () => {
    setNewSplits([{ fulfillingPharmacyId: '', quantityExpected: '' }]);
    setAddError(null);
    setAddShowError(false);
    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
    setAddError(null);
  };

  const handleNewSplitChange = (idx, field, value) =>
    setNewSplits((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));

  const handleAddNewSplitRow = () =>
    setNewSplits((prev) => [...prev, { fulfillingPharmacyId: '', quantityExpected: '' }]);

  const handleRemoveNewSplitRow = (idx) => setNewSplits((prev) => prev.filter((_, i) => i !== idx));

  const handleAddSave = () => {
    const allValid = newSplits.every(
      (s) => s.fulfillingPharmacyId.trim() !== '' && Number(s.quantityExpected) >= 1
    );
    if (!allValid || newSplitOverflow) {
      setAddShowError(true);
      return;
    }
    setAdding(true);
    setAddError(null);
    createInventoryReceivingLineSplitsApi({
      receivingLineId: line.receivingLineId,
      splits: newSplits.map((s) => ({
        fulfillingPharmacyId: s.fulfillingPharmacyId,
        quantityExpected: Number(s.quantityExpected),
      })),
    })
      .then((created) => {
        setSplits((prev) => [...prev, ...created]);
        handleCloseAdd();
      })
      .catch(() => setAddError('Failed to add distribution. Please try again.'))
      .finally(() => setAdding(false));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        >
          Stock Distribution
          <IconButton size='small' onClick={onClose} aria-label='Close'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pb: 3 }}>
          {/* Line info summary */}
          {line && (
            <Box
              sx={{
                backgroundColor: PRIMARY_LIGHT,
                borderLeft: `4px solid ${PRIMARY}`,
                borderRadius: 1,
                px: 2.5,
                py: 1.5,
                mb: 3,
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  NDC
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {line.ndc ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Lot #
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {line.lotNumber ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  BUD
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {line.bud ? format(new Date(line.bud), 'MM/dd/yyyy') : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Qty Ordered
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {line.quantityOrdered ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Qty Shipped
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {line.quantityShipped ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Qty Received
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {line.totalQuantityReceived ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Qty Allocated
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {loading ? '…' : existingAllocated}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Qty Unallocated
                </Typography>
                <Typography variant='body2' fontWeight={700} sx={{ color: availableQtyColor }}>
                  {loading || remaining === null ? '…' : remaining}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Splits list */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {fetchError && (
            <Typography color='error' variant='body2' sx={{ py: 2 }}>
              {fetchError}
            </Typography>
          )}

          {!loading && !fetchError && splits.length === 0 && (
            <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
              No splits found for this line.
            </Typography>
          )}

          {!loading && !fetchError && splits.length > 0 && (
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ backgroundColor: PRIMARY }}>
                  {[
                    'Fulfilling Pharmacy',
                    'Qty Expected',
                    'Qty Verified',
                    'Offage',
                    'Status',
                    'Verified At',
                    'verify-action',
                  ].map((header) => (
                    <TableCell key={header} sx={headCellSx}>
                      {header === 'verify-action' ? '' : header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {map(splits, (split, idx) => (
                  <TableRow
                    key={split.splitId}
                    sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f7fb' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {split.fulfillingPharmacy?.name ?? '—'}
                        {split.fulfillingPharmacy?.fulfillingPharmacyId ===
                          receivedAtPharmacyId && (
                          <Chip
                            label='Receiving'
                            size='small'
                            sx={{
                              bgcolor: '#031c57',
                              color: '#fff',
                              fontWeight: 600,
                              height: 18,
                              fontSize: 10,
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{split.quantityExpected}</TableCell>
                    <TableCell>{split.quantityVerified ?? '—'}</TableCell>
                    <TableCell>
                      {(() => {
                        const hasOffageReasons =
                          split.offage && split.offageReasons && split.offageReasons.length > 0;
                        if (!split.offage) return '—';
                        if (!hasOffageReasons)
                          return (
                            <Typography
                              variant='caption'
                              sx={{ color: '#ed6c02', fontWeight: 700 }}
                            >
                              -{split.offage}
                            </Typography>
                          );
                        return (
                          <Tooltip
                            placement='top'
                            arrow
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography
                                  variant='caption'
                                  sx={{
                                    fontWeight: 700,
                                    color: '#ed6c02',
                                    display: 'block',
                                    mb: 0.75,
                                  }}
                                >
                                  {split.offage} unit{split.offage !== 1 ? 's' : ''} offage
                                </Typography>
                                {split.offageReasons.map((r, rIdx) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <Box key={rIdx} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                                    <Chip
                                      label={`Qty ${r.qty}`}
                                      size='small'
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        bgcolor: '#ed6c02',
                                        color: '#fff',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ lineHeight: 1.4 }}
                                    >
                                      {r.reasons.join(' · ')}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            }
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'background.paper',
                                  color: 'text.primary',
                                  border: '1px solid #ffd18a',
                                  borderLeft: '3px solid #ed6c02',
                                  boxShadow: 3,
                                  '& .MuiTooltip-arrow': { color: '#ffd18a' },
                                  maxWidth: 300,
                                  p: 1,
                                },
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                              }}
                            >
                              <Typography
                                variant='caption'
                                sx={{ color: '#ed6c02', fontWeight: 700 }}
                              >
                                -{split.offage}
                              </Typography>
                              <WarningAmberIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={split.status.replace(/_/g, ' ')}
                        size='small'
                        sx={
                          SPLIT_STATUS_STYLES[split.status] ?? {
                            bgcolor: 'grey.500',
                            color: '#fff',
                          }
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {split.verifiedAt
                        ? format(new Date(split.verifiedAt), 'MM/dd/yyyy hh:mm a')
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>
                      {split.status === 'PENDING_VERIFICATION' && (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Tooltip title='Edit qty expected' arrow>
                            <IconButton
                              size='small'
                              onClick={() => handleOpenEdit(split)}
                              aria-label='Edit quantity expected'
                              sx={{ display: 'none' }}
                            >
                              <EditOutlinedIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          {hasAccess.verifyReceivingSplit &&
                            (() => {
                              // Confirming stock arrived somewhere is the destination's call, not
                              // the sending site's. Disabled rather than hidden, so the row still
                              // says the step exists and is waiting on someone else.
                              const isOwnFacility = userFacilityIds.has(
                                split.fulfillingPharmacy?.fulfillingPharmacyId
                              );

                              return (
                                <Tooltip
                                  title={
                                    isOwnFacility
                                      ? ''
                                      : 'Only a user with access to this facility can verify it'
                                  }
                                >
                                  <span>
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      color='success'
                                      disabled={!isOwnFacility}
                                      startIcon={<CheckCircleOutlineIcon fontSize='small' />}
                                      onClick={() => handleOpenVerify(split)}
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      Verify
                                    </Button>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Box
            sx={{ display: 'none', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
              Add Distribution
            </Typography>
            {!addOpen && (
              <Button
                size='small'
                variant='outlined'
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
              >
                Add Distribution
              </Button>
            )}
          </Box>

          {addOpen && (
            <Box sx={{ p: 1.5, border: '1px solid #e0e7f3', borderRadius: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Distribution{newSplits.length > 0 ? ` (${newSplits.length})` : ''}
                  </Typography>
                  {remaining !== null && (
                    <Typography
                      variant='caption'
                      sx={{ color: newSplitOverflow ? 'error.main' : 'text.secondary' }}
                    >
                      Remaining: {Math.max(0, remaining - newSplitTotal)}
                      {newSplitOverflow && ' — over-allocated'}
                    </Typography>
                  )}
                </Box>
                <Button size='small' startIcon={<AddIcon />} onClick={handleAddNewSplitRow}>
                  Add Row
                </Button>
              </Box>

              {/* eslint-disable-next-line react/no-array-index-key */}
              {newSplits.map((split, idx) => {
                const usedIds = newSplits
                  .filter((_, i) => i !== idx)
                  .map((s) => s.fulfillingPharmacyId);
                const available = fulfillingPharmacies.filter(
                  (p) =>
                    !usedIds.includes(p.fulfillingPharmacyId) ||
                    p.fulfillingPharmacyId === split.fulfillingPharmacyId
                );
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid key={idx} container spacing={1} alignItems='flex-start' sx={{ mb: 0.5 }}>
                    <Grid item xs={7}>
                      <FormControl
                        size='small'
                        fullWidth
                        error={addShowError && !split.fulfillingPharmacyId}
                      >
                        <InputLabel>Facility *</InputLabel>
                        <Select
                          label='Facility *'
                          value={split.fulfillingPharmacyId}
                          onChange={(e) =>
                            handleNewSplitChange(idx, 'fulfillingPharmacyId', e.target.value)
                          }
                        >
                          {available.map(({ fulfillingPharmacyId, fulfillingPharmacyName }) => (
                            <MenuItem key={fulfillingPharmacyId} value={fulfillingPharmacyId}>
                              {fulfillingPharmacyName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label='Qty Expected *'
                        size='small'
                        fullWidth
                        type='number'
                        inputProps={{ min: 1 }}
                        value={split.quantityExpected}
                        onChange={(e) =>
                          handleNewSplitChange(idx, 'quantityExpected', e.target.value)
                        }
                        error={addShowError && Number(split.quantityExpected) < 1}
                        helperText={
                          addShowError && Number(split.quantityExpected) < 1 ? 'Min 1' : ''
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={1}
                      sx={{ display: 'flex', alignItems: 'center', pt: '6px !important' }}
                    >
                      <IconButton
                        size='small'
                        color='error'
                        onClick={() => handleRemoveNewSplitRow(idx)}
                        disabled={newSplits.length === 1}
                        aria-label='Remove distribution row'
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}

              {newSplitOverflow && (
                <Typography variant='caption' color='error' sx={{ display: 'block', mt: 0.5 }}>
                  Total distribution qty ({existingAllocated + newSplitTotal}) exceeds received qty
                  ({line?.totalQuantityReceived}).
                </Typography>
              )}

              {addError && (
                <Typography color='error' variant='caption' sx={{ display: 'block', mt: 1 }}>
                  {addError}
                </Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                <Button size='small' color='secondary' onClick={handleCloseAdd}>
                  Cancel
                </Button>
                <LoadingButton
                  size='small'
                  variant='contained'
                  loading={adding}
                  onClick={handleAddSave}
                >
                  Save
                </LoadingButton>
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
          <Button
            size='medium'
            variant='text'
            color='info'
            startIcon={<InfoOutlinedIcon />}
            sx={{ textTransform: 'none' }}
            onMouseEnter={(e) => setHowItWorksAnchor(e.currentTarget)}
            onMouseLeave={() => setHowItWorksAnchor(null)}
          >
            How it works
          </Button>
          <Popover
            open={Boolean(howItWorksAnchor)}
            anchorEl={howItWorksAnchor}
            onClose={() => setHowItWorksAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            disableRestoreFocus
            sx={{ pointerEvents: 'none' }}
            slotProps={{
              paper: {
                sx: {
                  maxWidth: 520,
                  borderLeft: '4px solid',
                  borderColor: 'info.main',
                  p: 2,
                  pointerEvents: 'none',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <InfoOutlinedIcon fontSize='small' sx={{ color: 'info.main' }} />
              <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                How it works
              </Typography>
            </Box>
            <Typography
              variant='body2'
              color='text.secondary'
              component='ol'
              sx={{ m: 0, pl: 2.5 }}
            >
              <li style={{ marginBottom: 6 }}>
                Each distribution entry allocates a portion of the received stock to a fulfilling
                facility.
              </li>
              <li style={{ marginBottom: 6 }}>
                Use <strong>Confirm Qty Received</strong> to record the actual quantity that arrived
                at each facility, which may differ from the expected amount.
              </li>
              <li>
                Use <strong>Add Distribution</strong> at the bottom to assign stock to additional
                facilities.
              </li>
            </Typography>
          </Popover>
          <Button size='medium' variant='outlined' onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit qty expected dialog */}
      <Dialog open={Boolean(editTarget)} onClose={handleCloseEdit} maxWidth='xs' fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        >
          Edit Qty Expected
          <IconButton size='small' onClick={handleCloseEdit} aria-label='Close'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2, pb: 3 }}>
          {editTarget && (
            <Box
              sx={{
                backgroundColor: PRIMARY_LIGHT,
                borderLeft: `4px solid ${PRIMARY}`,
                borderRadius: 1,
                px: 2,
                py: 1.5,
                mb: 2.5,
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                Facility
              </Typography>
              <Typography variant='body2' fontWeight={600}>
                {editTarget.fulfillingPharmacy?.name ?? '—'}
              </Typography>
            </Box>
          )}

          <TextField
            label='Qty Expected *'
            size='small'
            fullWidth
            type='number'
            inputProps={{ min: 1, readOnly: !hasAccess.editSplitQty }}
            disabled={!hasAccess.editSplitQty}
            value={editQty}
            onChange={(e) => setEditQty(e.target.value)}
            error={editQtyInvalid}
            helperText={editQtyInvalid ? 'Must be ≥ 1' : ''}
          />

          {editError && (
            <Typography color='error' variant='caption' sx={{ display: 'block', mt: 1 }}>
              {editError}
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
            <Button size='small' color='secondary' onClick={handleCloseEdit}>
              Cancel
            </Button>
            <LoadingButton
              size='small'
              variant='contained'
              loading={editing}
              disabled={
                !hasAccess.editSplitQty || String(editQty).trim() === '' || Number(editQty) < 1
              }
              onClick={handleEditSave}
            >
              Save
            </LoadingButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Verify split dialog */}
      <Dialog open={Boolean(verifyTarget)} onClose={handleCloseVerify} maxWidth='sm' fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        >
          Confirm Qty Received
          <IconButton size='small' onClick={handleCloseVerify} aria-label='Close'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2, pb: 3 }}>
          {verifyTarget && (
            <Box
              sx={{
                backgroundColor: PRIMARY_LIGHT,
                borderLeft: `4px solid ${PRIMARY}`,
                borderRadius: 1,
                px: 2,
                py: 1.5,
                mb: 2.5,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Facility
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {verifyTarget.fulfillingPharmacy?.name ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Qty Expected
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {verifyTarget.quantityExpected}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  NDC
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                  {line?.ndc ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Lot #
                </Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                  {line?.lotNumber ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  BUD
                </Typography>
                <Typography variant='body2' fontWeight={600}>
                  {line?.bud ? format(new Date(line.bud), 'MM/dd/yyyy') : '—'}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ maxWidth: 180, mt: 2.5 }}>
            <TextField
              label='Qty Verified *'
              size='small'
              fullWidth
              type='number'
              inputProps={{ min: 0 }}
              value={verifyQty}
              onChange={(e) => {
                const val = e.target.value;
                setVerifyQty(val);
                const newOffage =
                  verifyTarget?.quantityExpected != null
                    ? verifyTarget.quantityExpected - (Number(val) || 0)
                    : 0;
                if (newOffage > 0 && verifyOffageReasons.length === 0) {
                  setVerifyOffageReasons([newVerifyOffageEntry()]);
                }
              }}
              error={verifyQtyInvalid}
              color={verifyQtyOverExpected ? 'error' : 'primary'}
              helperText={verifyQtyHelperText}
              FormHelperTextProps={{ sx: verifyQtyOverExpected ? { color: 'error.main' } : {} }}
            />
          </Box>

          {verifyUnverified && (
            <Box
              sx={{
                mt: 2,
                px: 2,
                py: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
              data-testid='verify-tally-section'
            >
              <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.25 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formatUnverifiedQty(verifyUnverified.unverifiedQty)} units of {line?.lotNumber}{' '}
                  {SYSTEM_DEFICIT.toLowerCase()} at{' '}
                  {verifyTarget?.fulfillingPharmacy?.name ?? 'this facility'}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {canSettleUnverified && verifyTallyComplete && (
                  <Chip
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                    label={`Tallied ${formatUnverifiedQty(verifyTallyState.effective)}`}
                    size='small'
                    variant='outlined'
                    color='success'
                  />
                )}
              </Stack>

              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: canSettleUnverified ? 1.5 : 0 }}
              >
                {/* Why this is being asked at all. Those units were dispensed against this lot
                    before inventory ever received it, so the claim was posted instead of a
                    decrement — and this delivery is the evidence it was real. */}
                These units were dispensed from this lot before it was ever received. Confirming
                this delivery is what closes that claim.
              </Typography>

              {!canSettleUnverified && (
                <Typography
                  variant='caption'
                  sx={{ display: 'block', color: 'warning.dark' }}
                  data-testid='verify-tally-blocked'
                >
                  This line has no BUD, so the claim cannot be settled here — the expiration date is
                  part of the lot&apos;s identity. Correct it in Inventory instead.
                </Typography>
              )}

              {canSettleUnverified && (
                <>
                  <Grid container columnSpacing={2} rowSpacing={1.5} alignItems='flex-start'>
                    <Grid item xs={4}>
                      <TextField
                        label={`Already dispensed (max ${verifyTallyState.max}) *`}
                        size='small'
                        fullWidth
                        type='number'
                        inputProps={{
                          min: 0,
                          max: verifyTallyState.max,
                          step: 1,
                          'data-testid': 'verify-tally-qty',
                        }}
                        value={verifyTallyQty ?? verifyTallyState.suggested}
                        onChange={(e) => setVerifyTallyQty(e.target.value)}
                        error={Boolean(verifyTallyState.error)}
                        helperText={verifyTallyState.error || ''}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl size='small' fullWidth error={verifyTallyReasonMissing}>
                        <InputLabel id='verify-tally-reason-label'>Reason *</InputLabel>
                        <Select
                          labelId='verify-tally-reason-label'
                          id='verify-tally-reason'
                          label='Reason *'
                          value={verifyTallyReason}
                          onChange={(e) => setVerifyTallyReason(e.target.value)}
                          inputProps={{ 'data-testid': 'verify-tally-reason' }}
                        >
                          {TALLY_REASON_OPTIONS.map(({ code, label }) => (
                            <MenuItem key={code} value={code}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                        {verifyTallyReasonMissing && <FormHelperText>Required</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      {/* Notes are always optional: the reason CODE above is what the audit
                          report groups on, and this carries whatever the code cannot say. */}
                      <TextField
                        label='Notes'
                        size='small'
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        placeholder='Optional'
                        value={verifyTallyNotes}
                        onChange={(e) => setVerifyTallyNotes(e.target.value)}
                        inputProps={{ maxLength: 250, 'data-testid': 'verify-tally-notes' }}
                      />
                    </Grid>
                  </Grid>

                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 0.75, pl: 0.25 }}
                  >
                    {verifyTallyState.received === null
                      ? 'Enter Qty Verified to see how much reaches the shelf.'
                      : [
                          ['Verifying', verifyTallyState.received],
                          ['already dispensed', verifyTallyState.effective],
                          ['to shelf', verifyTallyState.toShelf],
                          [
                            `left as ${SYSTEM_DEFICIT.toLowerCase()}`,
                            verifyTallyState.leftUnverified,
                          ],
                        ]
                          .map(([label, value]) => `${label} ${formatPreviewQty(value)}`)
                          .join(' · ')}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {showVerifyOffageSection && (
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                mt: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px !important',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: colors.offageReasonsSurface,
                  borderRadius: '3px',
                  minHeight: '40px !important',
                  '& .MuiAccordionSummary-content': { my: '6px', alignItems: 'center', gap: 1 },
                }}
              >
                <WarningAmberIcon fontSize='small' sx={{ color: 'warning.main' }} />
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Offage Reasons
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  · {verifyReasonQtySum} of {verifyOffage} units accounted
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {verifyReasonsBalanced && verifyDirtyReasons.length > 0 && (
                  <Chip
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                    label='Balanced'
                    color='success'
                    size='small'
                    sx={{ mr: 1, height: 22, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
                  />
                )}
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 2, pb: 1.5 }}>
                {verifyOffageReasons.length === 0 && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Optionally document reasons for this offage.
                  </Typography>
                )}

                {verifyOffageReasons.map((entry, idx) => {
                  const otherQtySum = verifyOffageReasons.reduce(
                    (s, r, ri) => (ri !== idx ? s + (Number(r.qty) || 0) : s),
                    0
                  );
                  const maxQty = verifyOffage - otherQtySum;
                  const qtyNum = Number(entry.qty);
                  const qtyExceedsMax = String(entry.qty).trim() !== '' && qtyNum > maxQty;
                  const qtyInvalid = String(entry.qty).trim() !== '' && qtyNum < 1;

                  let qtyHelperText;
                  if (qtyInvalid) qtyHelperText = 'Min 1';
                  else if (qtyExceedsMax)
                    qtyHelperText = maxQty <= 0 ? 'Fully accounted for' : `Max ${maxQty}`;

                  return (
                    <Box key={entry.id} sx={{ mb: 2 }}>
                      <Grid container spacing={2} alignItems='flex-start'>
                        <Grid item xs={3}>
                          <TextField
                            label='Qty *'
                            size='small'
                            fullWidth
                            type='number'
                            inputProps={{ min: 1, style: { textAlign: 'right' } }}
                            value={entry.qty}
                            onChange={(e) => handleVerifyOffageReasonQtyChange(idx, e.target.value)}
                            error={qtyInvalid || qtyExceedsMax}
                            helperText={qtyHelperText}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <Autocomplete
                            multiple
                            freeSolo
                            disableCloseOnSelect
                            size='small'
                            options={OFFAGE_REASON_OPTIONS}
                            value={entry.reasons}
                            onChange={(_, newReasons) =>
                              handleVerifyOffageReasonTagsChange(idx, newReasons)
                            }
                            filterSelectedOptions
                            renderTags={(value, getTagProps) =>
                              value.map((option, i) => (
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                <Chip
                                  key={option}
                                  label={option}
                                  size='small'
                                  // eslint-disable-next-line react/jsx-props-no-spreading
                                  {...getTagProps({ index: i })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              <TextField {...params} label='Reasons' size='small' />
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={1}
                          sx={{ display: 'flex', alignItems: 'center', pt: '6px !important' }}
                        >
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleRemoveVerifyOffageReason(idx)}
                          >
                            <DeleteOutlineIcon fontSize='small' />
                          </IconButton>
                        </Grid>
                      </Grid>
                      {idx < verifyOffageReasons.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  );
                })}

                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<AddIcon />}
                  onClick={handleAddVerifyOffageReason}
                >
                  Add reason
                </Button>
              </AccordionDetails>
            </Accordion>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
            <Button size='small' color='secondary' onClick={handleCloseVerify}>
              Cancel
            </Button>
            <LoadingButton
              size='small'
              variant='contained'
              color='success'
              loading={verifying}
              disabled={
                !verifyQtyFilled ||
                Number(verifyQty) < 0 ||
                verifyQtyOverExpected ||
                !verifyOffageRequiredValid ||
                !verifyTallyComplete
              }
              onClick={handleVerifySave}
            >
              Confirm
            </LoadingButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

LineSplitsDialog.propTypes = {
  line: PropTypes.shape({
    receivingLineId: PropTypes.string,
    ndc: PropTypes.string,
    lotNumber: PropTypes.string,
    bud: PropTypes.string,
    quantityOrdered: PropTypes.number,
    quantityShipped: PropTypes.number,
    totalQuantityReceived: PropTypes.number,
  }),
  receivedAtPharmacyId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

LineSplitsDialog.defaultProps = {
  line: null,
  receivedAtPharmacyId: null,
};

export default LineSplitsDialog;
